import React, { useState } from 'react';
import { ArrowRight, X } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [activeModal, setActiveModal] = useState(null);

  const privacyPolicyContent = {
    title: 'Privacy Policy',
    sections: [
      {
        heading: null,
        content: 'At Quick Sync, we are committed to protecting your privacy and ensuring the security of your data. This Privacy Policy explains how Quick Sync collects, uses, and protects your information while maintaining our core values of transparency, privacy, and anonymity.'
      },
      {
        heading: '1. Information We Collect',
        content: 'Quick Sync may collect the following types of information to enhance functionality and user experience:',
        list: [
          'User-Provided Information: Data you voluntarily provide, such as your name, email address, and preferences.',
          'Usage Data: Information on how you interact with Quick Sync applications, including access times, features used, and device details.',
          'Anonymized Data: Aggregated or anonymized information that does not personally identify users but helps us improve our platform.'
        ]
      },
      {
        heading: '2. How We Use Your Information',
        content: 'We use the information collected to:',
        list: [
          'Enhance the functionality and security of Quick Sync applications.',
          'Provide customer support and improve user experience.',
          'Analyze trends and usage to develop new features.'
        ]
      },
      {
        heading: '3. Data Security',
        content: 'We implement industry-standard security measures to protect your data against unauthorized access, loss, or misuse. These include encryption, access controls, and regular security assessments.'
      },
      {
        heading: '4. Data Sharing and Disclosure',
        content: 'We do not sell or share personal data with third parties except in the following cases:',
        list: [
          'Service Providers: Trusted third-party partners who assist in operating our services under strict confidentiality agreements.'
        ]
      },
      {
        heading: '5. Your Choices and Rights',
        content: 'You have the right to:',
        list: [
          'Access, update, or delete your personal information.',
          'Opt-out of certain data collection practices.',
          'Request information about how your data is used.'
        ]
      },
      {
        heading: '6. Changes to This Policy',
        content: 'We may update this Privacy Policy periodically to reflect improvements or legal requirements. We will notify users of significant changes.'
      },
      {
        heading: '7. Contact Us',
        content: 'For any privacy-related questions or requests, please contact us at info@quicksync.me.'
      },
      {
        heading: null,
        content: 'By using Quick Sync applications, you agree to the terms of this Privacy Policy.'
      }
    ]
  };

  const termsOfServiceContent = {
    title: 'Terms of Service',
    sections: [
      {
        heading: null,
        content: 'Welcome to Quick Sync. By accessing or using our services, you agree to be bound by these Terms of Service. Please read them carefully.'
      },
      {
        heading: '1. Acceptance of Terms',
        content: 'By using Quick Sync applications, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy.'
      },
      {
        heading: '2. Use of Services',
        content: 'You agree to use Quick Sync services only for lawful purposes and in accordance with these Terms. You are responsible for maintaining the confidentiality of your account credentials.'
      },
      {
        heading: '3. Intellectual Property',
        content: 'All content, features, and functionality of Quick Sync services are owned by Quick Sync and are protected by international copyright, trademark, and other intellectual property laws.'
      },
      {
        heading: '4. User Content',
        content: 'You retain ownership of any content you create or share through Quick Sync. By using our services, you grant us a limited license to process and store your content as necessary to provide our services.'
      },
      {
        heading: '5. Limitation of Liability',
        content: 'Quick Sync shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use our services.'
      },
      {
        heading: '6. Changes to Terms',
        content: 'We reserve the right to modify these Terms at any time. We will notify users of significant changes. Continued use of our services after changes constitutes acceptance of the new Terms.'
      },
      {
        heading: '7. Contact Us',
        content: 'For any questions regarding these Terms of Service, please contact us at info@quicksync.me.'
      }
    ]
  };

  const footerLinks = {
    products: [
      { name: 'QuickSync Me', href: 'https://quicksync.me/' },
      { name: 'StageX', href: 'https://stagex.space/' },
      { name: 'QS Framework', href: 'https://docs.quicksync.me/qs-framework' },
    ],
    developers: [
      { name: 'Documentation', href: 'https://docs.quicksync.me/qs-framework' },
      { name: 'APIs', href: 'https://docs.quicksync.me/qs-framework' },
      { name: 'Get Incubated', href: 'mailto:info@quicksync.me' },
    ],
    company: [
      { name: 'About', href: 'mailto:info@quicksync.me' },
      { name: 'News', href: 'https://medium.com/@quicksync' },
      { name: 'Contact', href: 'mailto:info@quicksync.me' },
    ],
    legal: [
      { name: 'Privacy Policy', action: () => setActiveModal('privacy') },
      { name: 'Terms of Service', action: () => setActiveModal('terms') },
    ],
  };

  const socialLinks = [
    { 
      name: 'X (Twitter)', 
      href: 'https://x.com/quicksyncme',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
        </svg>
      )
    },
    { 
      name: 'Telegram', 
      href: 'https://t.me/quick_sync',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
        </svg>
      )
    },
    { 
      name: 'Medium', 
      href: 'https://medium.com/@quicksync',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M13.54 12a6.8 6.8 0 01-6.77 6.82A6.8 6.8 0 010 12a6.8 6.8 0 016.77-6.82A6.8 6.8 0 0113.54 12zM20.96 12c0 3.54-1.51 6.42-3.38 6.42-1.87 0-3.39-2.88-3.39-6.42s1.52-6.42 3.39-6.42 3.38 2.88 3.38 6.42M24 12c0 3.17-.53 5.75-1.19 5.75-.66 0-1.19-2.58-1.19-5.75s.53-5.75 1.19-5.75C23.47 6.25 24 8.83 24 12z"/>
        </svg>
      )
    },
    { 
      name: 'GitHub', 
      href: 'https://github.com/QuickSyncLabs',
      icon: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
        </svg>
      )
    },
  ];

  return (
    <footer className="bg-gray-900 text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-8 lg:gap-12">
          {/* Brand Column */}
          <div className="col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img 
                src="https://customer-assets.emergentagent.com/job_f66dc093-173f-4bcc-bf21-1c61e3bc9a7a/artifacts/ng9lrvuu_Mask%20group%20%281%29.png"
                alt="QuickSync"
                className="h-10 w-auto"
              />
            </div>
            <p className="text-gray-400 text-sm mb-6 max-w-xs">
              Empowering everyone with private workspaces and InteractionFi. Build, share, and collaborate without compromise.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              {socialLinks.map((social, index) => (
                <a
                  key={index}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-gray-800 hover:bg-emerald-600 flex items-center justify-center text-gray-400 hover:text-white transition-all duration-300"
                  aria-label={social.name}
                >
                  {social.icon}
                </a>
              ))}
            </div>
          </div>

          {/* Products */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Products</h4>
            <ul className="space-y-3">
              {footerLinks.products.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-emerald-400 text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Developers */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Developers</h4>
            <ul className="space-y-3">
              {footerLinks.developers.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-emerald-400 text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Company</h4>
            <ul className="space-y-3">
              {footerLinks.company.map((link, index) => (
                <li key={index}>
                  <a
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-emerald-400 text-sm transition-colors"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h4 className="text-sm font-semibold text-white uppercase tracking-wider mb-4">Legal</h4>
            <ul className="space-y-3">
              {footerLinks.legal.map((link, index) => (
                <li key={index}>
                  <button
                    onClick={link.action}
                    className="text-gray-400 hover:text-emerald-400 text-sm transition-colors text-left"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Newsletter / CTA Section */}
        <div className="mt-16 pt-8 border-t border-gray-800">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h4 className="text-lg font-semibold text-white mb-1">Stay updated</h4>
              <p className="text-gray-400 text-sm">Get the latest news and updates from QuickSync.</p>
            </div>
            <a
              href="https://medium.com/@quicksync"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 group"
            >
              Read our Blog
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-800">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {currentYear} QuickSyncLabs. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-gray-500">
              <span>Built with privacy in mind</span>
              <span className="hidden md:inline">•</span>
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></span>
                All systems operational
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      {activeModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => setActiveModal(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-teal-50">
              <h2 className="text-2xl font-bold text-gray-900">
                {activeModal === 'privacy' ? privacyPolicyContent.title : termsOfServiceContent.title}
              </h2>
              <button
                onClick={() => setActiveModal(null)}
                className="w-10 h-10 rounded-full bg-white hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors shadow-sm"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {(activeModal === 'privacy' ? privacyPolicyContent : termsOfServiceContent).sections.map((section, index) => (
                <div key={index} className="mb-6 last:mb-0">
                  {section.heading && (
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{section.heading}</h3>
                  )}
                  <p className="text-gray-600 leading-relaxed">{section.content}</p>
                  {section.list && (
                    <ul className="mt-3 space-y-2">
                      {section.list.map((item, itemIndex) => (
                        <li key={itemIndex} className="text-gray-600 pl-4 relative before:content-['•'] before:absolute before:left-0 before:text-emerald-500">
                          {item}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => setActiveModal(null)}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white py-3 rounded-lg font-medium transition-colors"
              >
                I Understand
              </button>
            </div>
          </div>
        </div>
      )}
    </footer>
  );
};

export default Footer;
